"use client";
import React, { useState, useEffect, useCallback, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  getLocalCart,
  updateLocalCartItem,
  removeLocalCartItem,
  clearLocalCart,
  type LocalCartItem,
} from "@/lib/cartUtils";
import CartProductModal, { type CartItem as ModalCartItem } from "@/lib/CartProductModal";

// ─── Types ────────────────────────────────────────────────────────────────────
interface CartItem {
  cartItemId:      number;
  productId:       number;
  variantId?:      number | null;
  title:           string;
  sku:             string | null;
  color:           string | null;
  size:            string | null;
  price:           number;
  salePrice:       number | null;
  discountPercent?: number;
  quantity:        number;
  lineTotal:       number;
  image:           string | null;
  colorImage?:     string | null;
  selectedImage?:  string | null;
  inStock:         boolean;
  stock?:          number;
  variantHex?:     string | null;
  brand?:          string | null;
  rating?:         number | null;
  reviewCount?:    number | null;
  material?:       string | null;
  slug?:           string | null;
  availability?:   "available" | "unavailable";
  badges?: {
    isNew:        boolean;
    isTrending:   boolean;
    isFeatured:   boolean;
    isBestSeller: boolean;
  };
  delivery?:     string;
  returnPolicy?: string;
}

interface CartData {
  cartId:     number;
  currency:   string;
  items:      CartItem[];
  subtotal:   number;
  discount?:  number;
  tax?:       number;
  shipping?:  number;
  grandTotal?: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const API_BASE = process.env.NEXT_PUBLIC_API_URL || "/api";

function getToken() {
  return (
    sessionStorage.getItem("auth_token") ||
    localStorage.getItem("auth_token")   ||
    sessionStorage.getItem("token")      ||
    localStorage.getItem("token")        ||
    null
  );
}

function fmt(n: number) {
  return n.toLocaleString("en-IN");
}

function resolveItemImage(item: CartItem): string | null {
  return item.colorImage || item.selectedImage || item.image || null;
}

function computeSubtotal(items: CartItem[]): number {
  return Math.round(items.reduce((s, i) => s + i.lineTotal, 0) * 100) / 100;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ProductImage({ src, alt }: { src: string | null; alt: string }) {
  const [errored, setErrored] = useState(false);
  if (!src || errored) {
    return (
      <div className="w-20 h-24 bg-neutral-100 rounded-xl border border-neutral-200 flex items-center justify-center shrink-0">
        <span className="text-[9px] uppercase tracking-widest text-neutral-400 font-bold text-center px-1">
          No Image
        </span>
      </div>
    );
  }
  const fullSrc = src.startsWith("http") ? src : `${API_BASE.replace("/api", "")}${src}`;
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={fullSrc}
      alt={alt}
      onError={() => setErrored(true)}
      className="w-20 h-24 object-cover rounded-xl border border-neutral-200 shrink-0"
    />
  );
}

function BadgePill({ label }: { label: string }) {
  return (
    <span className="text-[8px] bg-neutral-900 text-white px-1.5 py-0.5 rounded font-black uppercase tracking-wider">
      {label}
    </span>
  );
}

function ItemBadges({ badges }: { badges?: CartItem["badges"] }) {
  if (!badges) return null;
  const active = [
    badges.isBestSeller && "Bestseller",
    badges.isNew        && "New",
    badges.isTrending   && "Trending",
    badges.isFeatured   && "Featured",
  ].filter(Boolean) as string[];
  if (!active.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {active.map((label) => <BadgePill key={label} label={label} />)}
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="p-6 border-2 border-dashed border-neutral-200 rounded-2xl bg-white animate-pulse flex gap-4 items-center">
      <div className="w-20 h-24 bg-neutral-100 rounded-xl shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-neutral-200 rounded w-3/4" />
        <div className="h-3 bg-neutral-200 rounded w-1/4" />
      </div>
      <div className="h-8 w-20 bg-neutral-200 rounded-xl" />
    </div>
  );
}

function LoginGateBanner() {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <p className="font-black text-sm text-amber-900 mb-1">Sign in to checkout</p>
          <p className="text-xs text-amber-700 mb-3">
            Your bag is saved locally. Create an account or sign in to place your order — your items will still be here.
          </p>
          <div className="flex gap-2 flex-wrap">
            <Link href="/login">
              <button className="bg-neutral-900 text-white text-[11px] font-black uppercase tracking-widest px-4 py-2 rounded-lg hover:bg-neutral-700 transition">
                Sign In
              </button>
            </Link>
            <Link href="/registration">
              <button className="border border-neutral-300 text-neutral-700 text-[11px] font-black uppercase tracking-widest px-4 py-2 rounded-lg hover:bg-neutral-50 transition">
                Create Account
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function CartPage() {
  const router = useRouter();

  const [cart,          setCart]          = useState<CartData | null>(null);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState<string | null>(null);
  const [updating,      setUpdating]      = useState<Set<number>>(new Set());
  const [removing,      setRemoving]      = useState<Set<number>>(new Set());
  const [toast,         setToast]         = useState<string | null>(null);
  const [isGuest,       setIsGuest]       = useState(false);
  // ── Modal state ──
  const [modalItem,     setModalItem]     = useState<CartItem | null>(null);


  const hasMergedLocalCart = useRef(false);

  // ── Toast ──────────────────────────────────────────────────────────────────
  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);

  // ── Local cart helpers ──────────────────────────────────────────────────────
  function localToCartItem(item: LocalCartItem): CartItem {
    return { ...item };
  }

  function buildLocalCartData(items: LocalCartItem[]): CartData {
    const cartItems = items.map(localToCartItem);
    return {
      cartId:   0,
      currency: "INR",
      items:    cartItems,
      subtotal: cartItems.reduce((s, i) => s + i.lineTotal, 0),
    };
  }

  const mergeLocalCartIntoServer = useCallback(async (token: string) => {
    if (hasMergedLocalCart.current) return false;

    const localItems = getLocalCart();
    if (!localItems.length) {
      hasMergedLocalCart.current = true; 
      return false;
    }


    hasMergedLocalCart.current = true;

    await Promise.allSettled(
      localItems.map((li) =>
        fetch(`${API_BASE}/cart/add`, {
          method:  "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body:    JSON.stringify({
            product_id: li.productId,
            size:       li.size,
            color:      li.color,
            quantity:   li.quantity,
          }),
        })
      )
    );

    clearLocalCart();
    return true;
  }, []);

  // ── Fetch cart ──────────────────────────────────────────────────────────────
 
  const fetchCart = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent;
    if (!silent) setLoading(true);
    setError(null);

    const token = getToken();

    if (!token) {
      setIsGuest(true);
      setCart(buildLocalCartData(getLocalCart()));
      if (!silent) setLoading(false);
      return;
    }

    setIsGuest(false);
    try {
      const merged = await mergeLocalCartIntoServer(token);

      const res = await fetch(`${API_BASE}/cart`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401) {
        setIsGuest(true);
        setCart(buildLocalCartData(getLocalCart()));
        if (!silent) setLoading(false);
        return;
      }
      if (!res.ok) throw new Error(`Server error ${res.status}`);

      const json = await res.json();
      if (!json.success) throw new Error(json.message || "Failed to load cart");

      setCart(json.data);

      if (merged) showToast("Saved items added to your bag.");
    } catch (err: unknown) {
      const localItems = getLocalCart();
      if (localItems.length > 0) {
        setIsGuest(true);
        setCart(buildLocalCartData(localItems));
        setError("Couldn't reach the server — showing your saved bag.");
      } else {
        setError(err instanceof Error ? err.message : "Something went wrong");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [mergeLocalCartIntoServer, showToast]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  // ── Update quantity ─────────────────────────────────────────────────────────
  async function updateQty(cartItemId: number, newQty: number) {
    setUpdating((prev) => new Set(prev).add(cartItemId));

    if (isGuest || cartItemId < 0) {
      updateLocalCartItem(cartItemId, newQty);
      setCart((prev) => {
        if (!prev) return prev;
        const items = prev.items.map((i) => {
          if (i.cartItemId !== cartItemId) return i;
          const ep = i.salePrice ?? i.price;
          return { ...i, quantity: newQty, lineTotal: newQty * ep };
        });
        return { ...prev, items, subtotal: computeSubtotal(items) };
      });
      setUpdating((prev) => { const n = new Set(prev); n.delete(cartItemId); return n; });
      return;
    }

    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/cart/item/${cartItemId}`, {
        method:  "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body:    JSON.stringify({ quantity: newQty }),
      });
      if (!res.ok) throw new Error("Failed to update quantity");

      await fetchCart({ silent: true });
    } catch {
      showToast("Could not update quantity. Try again.");
    } finally {
      setUpdating((prev) => { const n = new Set(prev); n.delete(cartItemId); return n; });
    }
  }

  // ── Remove item ─────────────────────────────────────────────────────────────
  async function removeItem(cartItemId: number, title: string) {
    setRemoving((prev) => new Set(prev).add(cartItemId));

    if (isGuest || cartItemId < 0) {
      removeLocalCartItem(cartItemId);
      setCart((prev) => {
        if (!prev) return prev;
        const items = prev.items.filter((i) => i.cartItemId !== cartItemId);
        return { ...prev, items, subtotal: computeSubtotal(items) };
      });
      setRemoving((prev) => { const n = new Set(prev); n.delete(cartItemId); return n; });
      showToast(`"${title}" removed from bag`);
      return;
    }

    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/cart/item/${cartItemId}`, {
        method:  "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to remove item");

      await fetchCart({ silent: true });
      showToast(`"${title}" removed from bag`);
    } catch {
      showToast("Could not remove item. Try again.");
    } finally {
      setRemoving((prev) => { const n = new Set(prev); n.delete(cartItemId); return n; });
    }
  }

  // ── Open product modal (on image or name click) ─────────────────────────────

  function openModal(item: CartItem) {
    setModalItem(item);
  }

  // ── Modal: variant updated (PATCH /variant, or localStorage for guests) ────
  async function handleCartUpdated(updated: Partial<CartItem> & { cartItemId: number }) {
   
    setCart((prev) => {
      if (!prev) return prev;
      const items = prev.items.map((i) =>
        i.cartItemId === updated.cartItemId ? { ...i, ...updated } : i
      );
      return { ...prev, items, subtotal: computeSubtotal(items) };
    });
    if (modalItem) {
      setModalItem((prev) => prev ? { ...prev, ...updated } : prev);
    }
    
    if (!isGuest) {
      await fetchCart({ silent: true });
    }
    showToast("Cart updated!");
  }

  // ── Modal: new variant added (POST /cart/add, or localStorage for guests) ──
  async function handleNewVariantAdded(newItem: CartItem) {
    setCart((prev) => {
      if (!prev) return prev;
      const exists = prev.items.some((i) => i.cartItemId === newItem.cartItemId);
      const items = exists
        ? prev.items.map((i) => i.cartItemId === newItem.cartItemId ? { ...i, ...newItem } : i)
        : [...prev.items, newItem];
      return { ...prev, items, subtotal: computeSubtotal(items) };
    });
    if (!isGuest) {
      await fetchCart({ silent: true });
    }
    showToast("New variant added to bag!");
  }

  // ── Checkout ────────────────────────────────────────────────────────────────
  function handleCheckout() {
    const token = getToken();
    if (!token) {
      sessionStorage.setItem("checkout_redirect", "/user/checkout");
      router.push("/login?redirect=/user/checkout");
      return;
    }
    router.push("/user/checkout");
  }

  function handleDecrement(item: CartItem) {
    if (item.quantity <= 1) return;
    updateQty(item.cartItemId, item.quantity - 1);
  }
  function handleIncrement(item: CartItem) {
    if (item.stock != null && item.quantity >= item.stock) return;
    updateQty(item.cartItemId, item.quantity + 1);
  }

  const subtotal          = cart?.subtotal  ?? 0;
  const discount          = cart?.discount  ?? 0;
  const tax               = cart?.tax       ?? 0;
  const shipping          = cart?.shipping  ?? 0;
  const grandTotal        = cart?.grandTotal ?? subtotal;
  const hasOrderBreakdown = !isGuest && cart?.grandTotal != null;
  const items             = cart?.items ?? [];
  const token             = typeof window !== "undefined" ? getToken() : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-neutral-100 to-zinc-50 py-32 px-4 sm:px-6 lg:px-8 font-sans text-neutral-900 relative">
      <div className="max-w-5xl mx-auto">

        {/* Toast */}
        {toast && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-neutral-900 text-white text-[11px] font-bold uppercase tracking-widest px-5 py-3 rounded-xl shadow-xl">
            {toast}
          </div>
        )}

        {/* Header */}
        <div className="border-b border-neutral-200 pb-5 mb-8 text-left">
          <h1 className="text-3xl font-black mb-2 tracking-tight text-neutral-900 uppercase">Shopping Bag</h1>
          <p className="text-sm text-neutral-500 font-medium">
            {loading ? "Loading your bag…" : `${items.length} ${items.length !== 1 ? "items" : "item"} currently reserved.`}
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="grid gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-4">
              {[1, 2].map((n) => <SkeletonRow key={n} />)}
            </div>
            <div className="p-6 border-2 border-dashed border-neutral-200 rounded-2xl bg-white h-48 animate-pulse" />
          </div>
        )}

        {/* Error */}
        {!loading && error && items.length === 0 && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center max-w-md mx-auto">
            <p className="text-red-700 font-semibold text-lg mb-1">Unable to load bag</p>
            <p className="text-red-500 text-sm mb-4">{error}</p>
            <button onClick={() => fetchCart()} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition">
              Retry
            </button>
          </div>
        )}

        {/* Soft error */}
        {!loading && error && items.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 text-[12px] text-amber-700 font-medium">
            ⚠ {error}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && items.length === 0 && (
          <div className="text-center py-16 bg-white border border-dashed border-neutral-200 rounded-2xl max-w-md mx-auto">
            <p className="text-4xl mb-4">🛍️</p>
            <p className="text-neutral-800 font-bold text-xl mb-2">Your bag is empty</p>
            <p className="text-neutral-400 text-sm mb-4">Add luxury garments from our collections.</p>
            <Link href="/shop/women">
              <button className="bg-neutral-900 text-white text-[11px] font-black uppercase tracking-widest px-5 py-2.5 rounded-lg hover:bg-neutral-700 transition">
                Shop Women's Collection
              </button>
            </Link>
          </div>
        )}

        {/* Cart content */}
        {!loading && items.length > 0 && (
          <div className="grid gap-8 lg:grid-cols-3 items-start">

            {/* LEFT: Items */}
            <div className="lg:col-span-2 space-y-4">

              {isGuest && <LoginGateBanner />}

              {items.map((item) => {
                const effectivePrice = item.salePrice ?? item.price;
                const isUpdating     = updating.has(item.cartItemId);
                const isRemoving     = removing.has(item.cartItemId);
                const itemImage      = resolveItemImage(item);
                const isLowStock     = item.stock != null && item.stock > 0 && item.stock <= 5;
                const atStockLimit   = item.stock != null && item.quantity >= item.stock;
                const isLocal        = item.cartItemId < 0;

                return (
                  <div
                    key={item.cartItemId}
                    className={`relative p-5 border-2 border-dashed border-neutral-300 rounded-2xl bg-white shadow-sm transition-all duration-300 hover:border-neutral-900 flex gap-4 items-start ${isRemoving ? "opacity-40 pointer-events-none" : ""}`}
                  >
                    {/* ── Clickable image → opens modal (guests included) ── */}
                    <button
                      onClick={() => openModal(item)}
                      className="shrink-0 rounded-xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:ring-offset-2 group/img"
                      aria-label={`View details for ${item.title}`}
                    >
                      <div className="relative">
                        <ProductImage src={itemImage} alt={item.title} />
                        <div className="absolute inset-0 rounded-xl bg-black/0 group-hover/img:bg-black/20 transition-colors flex items-center justify-center">
                          <span className="opacity-0 group-hover/img:opacity-100 transition-opacity text-white text-[8px] font-black uppercase tracking-widest bg-black/60 px-2 py-1 rounded">
                            View
                          </span>
                        </div>
                      </div>
                    </button>

                    <div className="flex-1 min-w-0">
                      {/* ── Clickable title → opens modal (guests included) ── */}
                      <button
                        onClick={() => openModal(item)}
                        className="text-left w-full focus:outline-none group/title disabled:cursor-default"
                      >
                        <h3 className="font-bold text-neutral-900 text-sm tracking-tight leading-snug line-clamp-2 group-hover/title:underline group-hover/title:underline-offset-2">
                          {item.title}
                        </h3>
                      </button>

                      <ItemBadges badges={item.badges} />

                      <div className="flex flex-wrap gap-2 mt-2">
                        {item.size && (
                          <span className="text-[9px] text-neutral-500 border border-neutral-200 bg-neutral-50 px-2 py-0.5 rounded font-black uppercase tracking-wider">
                            Size: {item.size}
                          </span>
                        )}
                        {item.color && (
                          <span className="flex items-center gap-1 text-[9px] text-neutral-500 border border-neutral-200 bg-neutral-50 px-2 py-0.5 rounded font-black uppercase tracking-wider">
                            {item.variantHex && (
                              <span className="w-2.5 h-2.5 rounded-full border border-neutral-300 inline-block" style={{ backgroundColor: item.variantHex }} />
                            )}
                            Col: {item.color}
                          </span>
                        )}
                        {item.sku && (
                          <span className="text-[9px] text-neutral-300 font-mono tracking-wide">SKU: {item.sku}</span>
                        )}
                        {isLocal && (
                          <span className="text-[9px] bg-amber-100 text-amber-600 border border-amber-200 px-2 py-0.5 rounded font-black uppercase tracking-wider">
                            Saved locally
                          </span>
                        )}
                      </div>

                      {/* Change variant CTA — available for guests too */}
                      {(item.color || item.size) && (
                        <button
                          onClick={() => openModal(item)}
                          className="mt-2 text-[9px] font-black text-[#b8955a] hover:text-neutral-900 underline underline-offset-2 transition uppercase tracking-wider"
                        >
                          Change Color / Size
                        </button>
                      )}

                      <div className="flex items-baseline gap-2 mt-3">
                        <p className="font-black text-sm text-neutral-900">₹{fmt(effectivePrice)}</p>
                        {item.salePrice && item.salePrice < item.price && (
                          <>
                            <p className="text-xs text-neutral-400 line-through font-bold">₹{fmt(item.price)}</p>
                            {!!item.discountPercent && (
                              <span className="text-[9px] bg-rose-50 text-rose-600 px-1.5 py-0.5 rounded font-black">
                                -{item.discountPercent}%
                              </span>
                            )}
                          </>
                        )}
                      </div>

                      <p className="text-[10px] text-neutral-400 font-bold uppercase tracking-wider mt-1.5 border-t border-neutral-50 pt-1.5">
                        Line total: <span className="text-neutral-800 font-black">₹{fmt(item.lineTotal)}</span>
                      </p>

                      {!item.inStock && (
                        <p className="text-[10px] font-black uppercase tracking-widest text-rose-600 mt-1">○ Out of stock</p>
                      )}
                      {item.inStock && isLowStock && (
                        <p className="text-[10px] font-black uppercase tracking-widest text-amber-600 mt-1">⚠ Only {item.stock} left</p>
                      )}
                      {item.delivery && (
                        <p className="text-[9px] text-neutral-400 mt-1.5 font-medium">{item.delivery}</p>
                      )}
                    </div>

                    {/* Quantity + Remove */}
                    <div className="flex flex-col items-end gap-4 shrink-0">
                      <div className={`flex items-center border-2 border-neutral-200 rounded-lg overflow-hidden bg-white shadow-sm ${isUpdating ? "opacity-50 pointer-events-none" : ""}`}>
                        <button
                          onClick={() => handleDecrement(item)}
                          disabled={item.quantity <= 1}
                          className="px-2.5 py-1 text-neutral-500 hover:bg-neutral-50 text-xs font-black disabled:opacity-30 transition select-none"
                        >−</button>
                        <span className="px-2 text-xs font-mono font-bold text-neutral-800 min-w-[1.2rem] text-center flex items-center justify-center">
                          {isUpdating
                            ? <span className="inline-block w-3 h-3 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
                            : item.quantity}
                        </span>
                        <button
                          onClick={() => handleIncrement(item)}
                          disabled={atStockLimit}
                          className="px-2.5 py-1 text-neutral-500 hover:bg-neutral-50 text-xs font-black disabled:opacity-30 transition select-none"
                        >+</button>
                      </div>

                      <button
                        onClick={() => removeItem(item.cartItemId, item.title)}
                        disabled={isRemoving}
                        className="text-[9px] font-black text-neutral-400 hover:text-rose-600 transition uppercase tracking-widest border border-neutral-200 px-2 py-1 rounded bg-white hover:bg-neutral-50 shadow-sm"
                      >
                        {isRemoving ? "Removing…" : "Remove"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* RIGHT: Summary */}
            <div className="p-6 border-2 border-dashed border-neutral-300 bg-white rounded-2xl space-y-4 sticky top-4">
              <h2 className="text-sm font-black uppercase tracking-widest text-neutral-900 pb-2 border-b border-neutral-100">
                Order Summary
              </h2>

              <div className="bg-neutral-50 rounded-xl p-3.5 space-y-1.5 text-[10px] font-bold uppercase tracking-wider text-neutral-400 border border-neutral-100">
                <div className="flex justify-between">
                  <span>Bag subtotal</span>
                  <span className="text-neutral-700">₹{fmt(subtotal)}</span>
                </div>

                {hasOrderBreakdown && discount > 0 && (
                  <div className="flex justify-between">
                    <span>Discount</span>
                    <span className="text-emerald-600 font-black">−₹{fmt(discount)}</span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span className="text-emerald-600 font-black">
                    {hasOrderBreakdown ? (shipping > 0 ? `₹${fmt(shipping)}` : "FREE") : "FREE"}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span>Tax</span>
                  <span className="text-neutral-400 font-medium lowercase">
                    {hasOrderBreakdown ? `₹${fmt(tax)}` : "Calculated at checkout"}
                  </span>
                </div>

                <div className="flex justify-between text-neutral-900 pt-2 border-t border-neutral-200 text-xs font-black">
                  <span>Total</span>
                  <span>₹{fmt(hasOrderBreakdown ? grandTotal : subtotal)}</span>
                </div>
              </div>

              <div className="space-y-2 pt-2">
                <button
                  onClick={handleCheckout}
                  className="w-full flex items-center justify-center gap-2 bg-neutral-900 text-white py-2.5 rounded-lg text-xs font-bold uppercase tracking-widest shadow-sm hover:bg-neutral-700 transition active:scale-95"
                >
                  {!token ? (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Sign in to Checkout
                    </>
                  ) : "Proceed to Checkout"}
                </button>

                {!token && (
                  <p className="text-[10px] text-neutral-400 text-center tracking-wide">
                    Your bag is saved — sign in to place your order.
                  </p>
                )}

                <button
                  onClick={() => fetchCart()}
                  className="w-full border border-neutral-200 text-neutral-500 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest bg-white hover:bg-neutral-50 transition shadow-sm"
                >
                  Refresh Bag
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Product Modal ── */}
      {modalItem && (
        <CartProductModal
          cartItem={modalItem as ModalCartItem}
          onClose={() => setModalItem(null)}
          onCartUpdated={handleCartUpdated}
          onNewVariantAdded={handleNewVariantAdded}
        />
      )}
    </div>
  );
}